// Clinic invoices.
//
// This screen used to read the legacy finance module, whose invoices are foreign-keyed to the old
// `cases`/`clients` tables — so it could never show an invoice for a workflow case, and it listed
// nothing. It now shows the real ones: raised automatically when a case's aligner count is set, and
// mirrored onto the doctor's running account.
//
// Settlement is at ACCOUNT level, not per invoice — payments land against the doctor's account, so
// "what is still owed" is the balance on Billing, not a status on each row. The status column says
// only whether an invoice stands or was voided.
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDate, formatMoney, rupeesToPaisa, type WfInvoice } from '@alignova/shared';
import { PageHeader, Card, Pill } from '../../ui/primitives.tsx';
import { SkeletonList } from '../../ui/mobile.tsx';
import { useCan } from '../../auth/useCan.ts';
import { useLiveEvent } from '../../lib/useLive.ts';
import { IconSearch } from '../../ui/icons.tsx';
import { wfApi } from '../workflow/workflowApi.ts';
import { Btn, Modal, inputCls } from '../workflow/wfUi.tsx';

export function InvoicesPage() {
  const can = useCan();
  const [items, setItems] = useState<WfInvoice[] | null>(null);
  const [q, setQ] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [open, setOpen] = useState<WfInvoice | null>(null);
  const [catchingUp, setCatchingUp] = useState(false);
  const [caughtUp, setCaughtUp] = useState<string | null>(null);

  const load = useCallback(() => {
    wfApi
      .invoices(q.trim() || undefined)
      .then((r) => {
        setItems(r);
        setErr(null);
      })
      .catch((e: unknown) => setErr(e instanceof Error ? e.message : String(e)));
  }, [q]);
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);
  useLiveEvent('workflow.changed', load);

  // Raise the invoice for every priced case that has none (e.g. one whose count was set before it
  // had a clinic). Always available - not only on an empty list - because a single missed case is
  // exactly what an otherwise full list hides.
  const catchUp = () => {
    setCatchingUp(true);
    setCaughtUp(null);
    wfApi
      .backfillInvoices()
      .then((r) => {
        setCaughtUp(
          r.created > 0
            ? `Raised ${r.created} invoice${r.created === 1 ? '' : 's'}.`
            : 'Nothing to raise - every case with an aligner count already has its invoice.',
        );
        load();
      })
      .catch((e: unknown) => setCaughtUp(e instanceof Error ? e.message : String(e)))
      .finally(() => setCatchingUp(false));
  };

  const live = items?.filter((i) => i.status === 'issued') ?? [];
  const billed = live.reduce((a, i) => a + i.total, 0);

  return (
    <div>
      <PageHeader
        title="Invoices"
        subtitle="Raised automatically when a case's aligner count is set. Payments settle the doctor's account, not individual invoices."
        action={
          <div className="flex items-center gap-3">
            <span className="tabnum text-sm text-muted">
              {live.length} live · {formatMoney(billed)}
            </span>
            {can('invoice:manage') && !!items?.length && (
              <Btn tone="subtle" disabled={catchingUp} onClick={catchUp}>
                {catchingUp ? 'Working…' : 'Raise missing invoices'}
              </Btn>
            )}
          </div>
        }
      />
      {caughtUp && !!items?.length && <p className="mb-3 text-sm text-muted">{caughtUp}</p>}

      <div className="relative mb-3 max-w-md">
        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted">
          <IconSearch size={16} />
        </span>
        <input
          className="h-9 w-full rounded-md border border-line bg-surface-2/60 pl-9 pr-3 text-sm"
          placeholder="Invoice number, case, patient or doctor…"
          aria-label="Search invoices"
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>

      {err && <p className="mb-3 text-sm text-status-urgent-fg">{err}</p>}
      {!items && <SkeletonList rows={5} />}
      {items && !items.length && (
        <div className="rounded-lg border border-dashed border-line bg-surface/60 px-6 py-10 text-center">
          <p className="font-semibold text-ink">{q ? 'No invoices match that search.' : 'No invoices yet'}</p>
          {!q && (
            <>
              <p className="mx-auto mt-1 max-w-[52ch] text-sm text-muted">
                An invoice is raised the moment a case gets its number of aligners. Cases priced
                before invoicing existed don&rsquo;t have one - this catches them up.
              </p>
              {can('invoice:manage') && (
                <div className="mt-4">
                  <Btn
                    tone="accent"
                    disabled={catchingUp}
                    onClick={catchUp}
                  >
                    {catchingUp ? 'Working…' : 'Raise invoices for priced cases'}
                  </Btn>
                </div>
              )}
              {caughtUp && <p className="mt-2 text-sm text-muted">{caughtUp}</p>}
            </>
          )}
        </div>
      )}

      {items && items.length > 0 && (
        <Card className="overflow-hidden">
          <table className="w-full text-sm">
            <thead className="border-b border-line bg-surface-2">
              <tr>
                {['Invoice', 'Date', 'Doctor', 'Case', 'Amount', 'Status', ''].map((h) => (
                  <th key={h} className="px-3 py-2.5 text-left text-[12px] font-semibold text-muted">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((i) => (
                <tr key={i.id} className="border-t border-line transition-colors hover:bg-surface-2/50">
                  <td className="whitespace-nowrap px-3 py-2.5 font-mono font-semibold text-ink">{i.invoiceNumber}</td>
                  <td className="tabnum whitespace-nowrap px-3 py-2.5 text-muted">{formatDate(i.issueDate)}</td>
                  <td className="px-3 py-2.5">
                    <div className="truncate">{i.doctorName ?? i.clinicName ?? '-'}</div>
                  </td>
                  <td className="px-3 py-2.5">
                    {i.caseId ? (
                      <Link to={`/workflow/cases/${i.caseId}`} className="font-mono text-xs text-accent hover:underline">
                        {i.caseNumber}
                      </Link>
                    ) : (
                      <span className="text-muted">-</span>
                    )}
                    {i.patientName && <div className="truncate text-[11px] text-muted">{i.patientName}</div>}
                  </td>
                  <td className="tabnum whitespace-nowrap px-3 py-2.5 font-semibold text-ink">{formatMoney(i.total)}</td>
                  <td className="px-3 py-2.5">
                    <Pill tone={i.status === 'void' ? 'bad' : 'good'}>{i.status === 'void' ? 'Void' : 'Issued'}</Pill>
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    <div className="flex justify-end gap-1">
                      <Btn tone="ghost" onClick={() => setOpen(i)}>
                        View
                      </Btn>
                      <Link
                        to={`/finance/invoices/${i.id}/print`}
                        className="tap inline-flex items-center rounded-md border border-line px-2.5 py-1 text-xs font-medium text-ink hover:bg-surface-2"
                      >
                        Print
                      </Link>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <InvoiceModal
        invoice={open}
        canVoid={can('invoice:manage')}
        onClose={() => setOpen(null)}
        onVoided={() => {
          setOpen(null);
          load();
        }}
        onEdited={(next) => {
          setOpen(next);
          load();
        }}
      />
    </div>
  );
}

function InvoiceModal({
  invoice,
  canVoid,
  onClose,
  onVoided,
  onEdited,
}: {
  invoice: WfInvoice | null;
  canVoid: boolean;
  onClose: () => void;
  onVoided: () => void;
  onEdited: (next: WfInvoice) => void;
}) {
  const [reason, setReason] = useState('');
  const [voiding, setVoiding] = useState(false);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    setReason('');
    setVoiding(false);
    setEditing(false);
    setErr(null);
  }, [invoice]);

  if (!invoice) return null;
  if (editing) return <InvoiceEditModal invoice={invoice} onClose={() => setEditing(false)} onSaved={onEdited} />;

  const doVoid = async () => {
    setBusy(true);
    setErr(null);
    try {
      await wfApi.voidInvoice(invoice.id, reason.trim());
      onVoided();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={invoice.invoiceNumber}
      footer={
        <>
          <Btn tone="ghost" onClick={onClose}>
            Close
          </Btn>
          <Link
            to={`/finance/invoices/${invoice.id}/print`}
            className="tap inline-flex items-center rounded-md border border-line px-3 py-1.5 text-sm font-medium text-ink hover:bg-surface-2"
          >
            Print / PDF
          </Link>
          {canVoid && invoice.status === 'issued' && !voiding && (
            <>
              <Btn tone="subtle" onClick={() => setEditing(true)}>
                Edit
              </Btn>
              <Btn tone="danger" onClick={() => setVoiding(true)}>
                Void
              </Btn>
            </>
          )}
        </>
      }
    >
      <div className="space-y-4">
        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm sm:grid-cols-4">
          <Meta label="Issued" value={formatDate(invoice.issueDate)} />
          <Meta label="Doctor" value={invoice.doctorName ?? invoice.clinicName ?? '-'} />
          <Meta label="Case" value={invoice.caseNumber ?? '-'} />
          <Meta label="Patient" value={invoice.patientName ?? '-'} />
        </dl>

        <table className="w-full text-sm">
          <thead className="border-b border-line">
            <tr>
              <th className="py-2 text-left text-[12px] font-semibold text-muted">Description</th>
              <th className="py-2 text-right text-[12px] font-semibold text-muted">Qty</th>
              <th className="py-2 text-right text-[12px] font-semibold text-muted">Rate</th>
              <th className="py-2 text-right text-[12px] font-semibold text-muted">Amount</th>
            </tr>
          </thead>
          <tbody>
            {invoice.lines.map((l) => (
              <tr key={l.id} className="border-b border-line">
                <td className="py-2">{l.description}</td>
                <td className="tabnum py-2 text-right">{l.quantity}</td>
                <td className="tabnum py-2 text-right">{formatMoney(l.unitAmount)}</td>
                <td className="tabnum py-2 text-right font-medium">{formatMoney(l.amount)}</td>
              </tr>
            ))}
            <tr>
              <td colSpan={3} className="py-2 text-right font-semibold">
                Total
              </td>
              <td className="tabnum py-2 text-right text-base font-bold text-ink">{formatMoney(invoice.total)}</td>
            </tr>
          </tbody>
        </table>

        {invoice.status === 'void' && (
          <p className="rounded-md bg-status-urgent-bg px-3 py-2 text-sm text-status-urgent-fg">
            Voided{invoice.voidedAt ? ` ${formatDate(invoice.voidedAt)}` : ''}
            {invoice.voidReason ? ` - ${invoice.voidReason}` : ''}
          </p>
        )}

        {voiding && (
          <div className="rounded-md border border-status-urgent-fg/30 bg-status-urgent-bg/40 p-3">
            <p className="mb-2 text-sm text-status-urgent-fg">
              Voiding removes the charge from the doctor&rsquo;s account. The invoice and its number
              are kept - a number is never reused.
            </p>
            {err && <p className="mb-2 text-sm text-status-urgent-fg">{err}</p>}
            <input
              className={inputCls}
              placeholder="Why is it being voided?"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
            <div className="mt-2 flex gap-2">
              <Btn tone="ghost" onClick={() => setVoiding(false)}>
                Cancel
              </Btn>
              <Btn tone="danger" disabled={busy || reason.trim().length < 3} onClick={() => void doVoid()}>
                {busy ? 'Voiding…' : 'Void this invoice'}
              </Btn>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

interface DraftLine {
  key: number;
  description: string;
  quantity: string;
  /** Rupees as typed; negative for a discount. */
  rate: string;
}

/**
 * Edit an issued invoice. Saving issues the corrected invoice (new number) and voids this one with
 * the reason, in one step on the server - so the clinic's ledger and the books move with it and the
 * original stays on record. Amounts are typed in rupees; a negative rate is a discount line.
 */
function InvoiceEditModal({ invoice, onClose, onSaved }: { invoice: WfInvoice; onClose: () => void; onSaved: (next: WfInvoice) => void }) {
  const [lines, setLines] = useState<DraftLine[]>(() =>
    invoice.lines.map((l, i) => ({ key: i, description: l.description, quantity: String(l.quantity), rate: String(l.unitAmount / 100) })),
  );
  const [notes, setNotes] = useState(invoice.notes ?? '');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const parsed = lines.map((l) => ({ description: l.description.trim(), quantity: Number(l.quantity), unitAmount: rupeesToPaisa(l.rate) }));
  const valid = parsed.every((l) => l.description && Number.isInteger(l.quantity) && l.quantity >= 1 && l.unitAmount != null);
  const total = valid ? parsed.reduce((a, l) => a + l.quantity * (l.unitAmount ?? 0), 0) : null;
  const set = (key: number, k: keyof DraftLine, v: string) => setLines((ls) => ls.map((l) => (l.key === key ? { ...l, [k]: v } : l)));

  const [targetTotal, setTargetTotal] = useState('');
  const applyTotal = () => {
    const target = rupeesToPaisa(targetTotal);
    if (target == null || target < 0) return setErr('Enter the new total in rupees.');
    const base = lines.filter((l) => l.description.trim() !== 'Adjustment');
    const baseTotal = base.reduce((a, l) => a + (Number(l.quantity) || 0) * (rupeesToPaisa(l.rate) ?? 0), 0);
    const diff = target - baseTotal;
    const nextKey = Math.max(-1, ...lines.map((x) => x.key)) + 1;
    setLines(diff === 0 ? base : [...base, { key: nextKey, description: 'Adjustment', quantity: '1', rate: String(diff / 100) }]);
    setErr(null);
  };

  const save = async () => {
    if (!valid || total == null) return setErr('Every line needs a description, a whole-number quantity and a rate.');
    if (total < 0) return setErr('The total cannot be negative.');
    if (reason.trim().length < 3) return setErr('Say why the invoice is being changed.');
    setBusy(true);
    setErr(null);
    try {
      const next = await wfApi.editInvoice(invoice.id, {
        lines: parsed.map((l) => ({ description: l.description, quantity: l.quantity, unitAmount: l.unitAmount! })),
        notes: notes.trim() || null,
        reason: reason.trim(),
      });
      onSaved(next);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      wide
      onClose={onClose}
      title={`Edit ${invoice.invoiceNumber}`}
      footer={
        <>
          <Btn tone="ghost" onClick={onClose}>
            Cancel
          </Btn>
          <Btn disabled={busy} onClick={() => void save()}>
            {busy ? 'Saving…' : 'Save changes'}
          </Btn>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-muted">
          Saving issues the corrected invoice with a new number and voids {invoice.invoiceNumber} with your reason. The clinic&rsquo;s
          ledger and the accounts adjust automatically; the original stays on record. Use a negative rate for a discount.
        </p>
        <table className="w-full text-sm">
          <thead className="border-b border-line">
            <tr>
              <th className="py-2 text-left text-[12px] font-semibold text-muted">Description</th>
              <th className="w-20 py-2 text-right text-[12px] font-semibold text-muted">Qty</th>
              <th className="w-36 py-2 text-right text-[12px] font-semibold text-muted">Rate (PKR)</th>
              <th className="w-32 py-2 text-right text-[12px] font-semibold text-muted">Amount</th>
              <th className="w-8" />
            </tr>
          </thead>
          <tbody>
            {lines.map((l, i) => {
              const p = parsed[i]!;
              const amt = Number.isInteger(p.quantity) && p.unitAmount != null ? p.quantity * p.unitAmount : null;
              return (
                <tr key={l.key} className="border-b border-line">
                  <td className="py-1.5 pr-2">
                    <input className={inputCls} aria-label="Description" value={l.description} onChange={(e) => set(l.key, 'description', e.target.value)} />
                  </td>
                  <td className="py-1.5 pr-2">
                    <input className={`${inputCls} text-right`} aria-label="Quantity" inputMode="numeric" value={l.quantity} onChange={(e) => set(l.key, 'quantity', e.target.value)} />
                  </td>
                  <td className="py-1.5 pr-2">
                    <input className={`${inputCls} text-right`} aria-label="Rate in PKR" inputMode="decimal" value={l.rate} onChange={(e) => set(l.key, 'rate', e.target.value)} />
                  </td>
                  <td className="tabnum py-1.5 text-right font-medium">{amt != null ? formatMoney(amt) : '-'}</td>
                  <td className="py-1.5 text-right">
                    <button
                      type="button"
                      className="px-1 text-muted hover:text-status-urgent-fg disabled:opacity-30"
                      disabled={lines.length === 1}
                      aria-label="Remove line"
                      onClick={() => setLines((ls) => ls.filter((x) => x.key !== l.key))}
                    >
                      ✕
                    </button>
                  </td>
                </tr>
              );
            })}
            <tr>
              <td colSpan={2} className="py-2">
                <button
                  type="button"
                  className="text-sm font-medium text-accent hover:underline"
                  onClick={() => setLines((ls) => [...ls, { key: Math.max(-1, ...ls.map((x) => x.key)) + 1, description: '', quantity: '1', rate: '' }])}
                >
                  ＋ Add line
                </button>
              </td>
              <td className="py-2 text-right font-semibold">Total</td>
              <td className="tabnum py-2 text-right text-base font-bold text-ink">{total != null ? formatMoney(total) : '-'}</td>
              <td />
            </tr>
          </tbody>
        </table>
        {/* Just change the amount: one Adjustment line carries the difference from the real lines
            (the same rule the ledger's Edit uses, so both produce the same invoice). */}
        <div className="flex flex-wrap items-end gap-2 rounded-md border border-line bg-surface-2/50 p-3">
          <label className="text-sm">
            <span className="mb-1 block text-[12px] font-semibold text-muted">Change total to (PKR)</span>
            <input className={`${inputCls} w-40 text-right`} inputMode="decimal" value={targetTotal} onChange={(e) => setTargetTotal(e.target.value)} />
          </label>
          <Btn tone="subtle" onClick={applyTotal}>
            Apply
          </Btn>
          <span className="text-xs text-muted">Adds or updates an &ldquo;Adjustment&rdquo; line for the difference.</span>
        </div>
        <label className="block text-sm">
          <span className="mb-1 block text-[12px] font-semibold text-muted">Notes (printed on the invoice)</span>
          <textarea className={inputCls} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </label>
        <label className="block text-sm">
          <span className="mb-1 block text-[12px] font-semibold text-muted">Reason for the change *</span>
          <input className={inputCls} placeholder="e.g. price agreed with the doctor" value={reason} onChange={(e) => setReason(e.target.value)} />
        </label>
        {err && <p className="text-sm text-status-urgent-fg">{err}</p>}
      </div>
    </Modal>
  );
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted">{label}</dt>
      <dd className="truncate text-sm text-ink">{value}</dd>
    </div>
  );
}
